import { createSpecialistServicesAppointmentDraft } from "@/app/booking/adapters/specialistServicesAppointmentDraft";
import { createLocationServicesAppointmentDraft } from "@/app/booking/adapters/locationServicesAppointmentDraft";
import {
  isLocationGroupBookingEntryFeasibleFromCatalog,
  type LocationAppointmentCatalog,
} from "@/app/booking/adapters/locationAppointmentCatalog";
import {
  APPOINTMENT_DRAFT_SCHEMA_VERSION,
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
  type AppointmentServiceLineDraft,
} from "@/app/booking/core/appointmentBookingDraft";
import type {
  SpecialistAssignment,
  SpecialistService,
  SpecialistServiceOffering,
  SpecialistServiceSelection,
  SpecialistWorkplace,
} from "@/app/specialists/model";

export const SPECIALIST_GROUP_RESUME_DRAFT_PARAM =
  "group_booking_draft_id";
export const SPECIALIST_GROUP_RESUME_VERSION_PARAM =
  "group_booking_draft_version";

export interface SpecialistGroupBookingResumeHandoff {
  draftId: string;
  schemaVersion: typeof APPOINTMENT_DRAFT_SCHEMA_VERSION;
}

export interface SpecialistGroupBookingResumeGuest {
  participantId: string;
  selections: readonly SpecialistServiceSelection[];
}

export interface SpecialistGroupBookingResume {
  draftId: string;
  organizerSelections: readonly SpecialistServiceSelection[];
  groupGuests: readonly SpecialistGroupBookingResumeGuest[];
}

type ServicesGroupBookingResumeOrigin =
  | {
      kind: "specialist-services";
      assignment: SpecialistAssignment;
    }
  | {
      kind: "location-services";
      catalogSnapshot: LocationAppointmentCatalog;
      catalogAssignment: SpecialistAssignment;
      locationId: string;
    };

interface SearchParamsLike {
  getAll(name: string): string[];
}

function isCanonicalIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160
  );
}

function cloneSelection(
  selection: SpecialistServiceSelection,
): SpecialistServiceSelection {
  return {
    serviceId: selection.serviceId,
    variantId: selection.variantId,
    ...(selection.addOns && selection.addOns.length > 0
      ? {
          addOns: selection.addOns.map((addOn) => ({ ...addOn })),
        }
      : {}),
    ...(selection.bundleItems
      ? {
          bundleItems: selection.bundleItems.map((item) => ({
            ...item,
            addOns: item.addOns.map((addOn) => ({ ...addOn })),
          })),
        }
      : {}),
  };
}

export function getSpecialistSelectionFromAppointmentLine(
  line: AppointmentServiceLineDraft,
): SpecialistServiceSelection {
  return line.selection.kind === "service"
    ? {
        serviceId: line.selection.serviceId,
        variantId: line.selection.variantId,
        ...(line.selection.addOns.length > 0
          ? {
              addOns: line.selection.addOns.map((addOn) => ({
                ...addOn,
              })),
            }
          : {}),
      }
    : {
        serviceId: line.selection.serviceId,
        variantId: null,
        bundleItems: line.selection.bundleItems.map((item) => ({
          bundleItemId: item.bundleItemId,
          serviceId: item.serviceId,
          variantId: item.variantId,
          addOns: item.addOns.map((addOn) => ({ ...addOn })),
        })),
      };
}

function getOrderedSelections(
  lines: readonly AppointmentServiceLineDraft[],
) {
  return [...lines]
    .sort((left, right) => left.selectionOrder - right.selectionOrder)
    .map(getSpecialistSelectionFromAppointmentLine);
}

function hasSameSerializableStructure(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

export function parseSpecialistGroupBookingResumeHandoff(
  searchParams: SearchParamsLike,
): SpecialistGroupBookingResumeHandoff | null {
  const draftIds = searchParams.getAll(
    SPECIALIST_GROUP_RESUME_DRAFT_PARAM,
  );
  const versions = searchParams.getAll(
    SPECIALIST_GROUP_RESUME_VERSION_PARAM,
  );

  if (
    draftIds.length !== 1 ||
    versions.length !== 1 ||
    !isCanonicalIdentifier(draftIds[0]) ||
    versions[0] !== String(APPOINTMENT_DRAFT_SCHEMA_VERSION)
  ) {
    return null;
  }

  return {
    draftId: draftIds[0],
    schemaVersion: APPOINTMENT_DRAFT_SCHEMA_VERSION,
  };
}

export function appendSpecialistGroupBookingResumeHandoff(
  href: string,
  handoff: SpecialistGroupBookingResumeHandoff,
) {
  if (
    !href.startsWith("/") ||
    !isCanonicalIdentifier(handoff.draftId) ||
    handoff.schemaVersion !== APPOINTMENT_DRAFT_SCHEMA_VERSION
  ) {
    return null;
  }

  const url = new URL(href, "https://volera.local");
  url.searchParams.set(
    SPECIALIST_GROUP_RESUME_DRAFT_PARAM,
    handoff.draftId,
  );
  url.searchParams.set(
    SPECIALIST_GROUP_RESUME_VERSION_PARAM,
    String(handoff.schemaVersion),
  );

  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Revalidates a group resume pointer against its complete origin-owned
 * catalog. Location must preserve the route-owned eligibility snapshot; no
 * participant or selection is restored unless rebuilding the whole schema-v1
 * draft produces the exact stored structure.
 */
function resolveServicesGroupBookingResume(input: {
  draft: AppointmentBookingDraftV1;
  origin: ServicesGroupBookingResumeOrigin;
  workplace: SpecialistWorkplace;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
}): SpecialistGroupBookingResume | null {
  const validation = validateAppointmentStepOneBookingDraft(
    input.draft,
  );

  if (!validation.valid) {
    return null;
  }

  const draft = validation.draft;

  const hasExactOrigin =
    input.origin.kind === "specialist-services"
      ? draft.entryPoint.kind === "specialist-services" &&
        draft.entryPoint.locationId === input.origin.assignment.locationId &&
        draft.entryPoint.userLocationId ===
          input.origin.assignment.userLocationId
      : draft.entryPoint.kind === "location-services" &&
        draft.entryPoint.locationId === input.origin.locationId &&
        input.origin.catalogAssignment.locationId === input.origin.locationId &&
        input.origin.catalogSnapshot.workplace.locationId ===
          input.origin.locationId &&
        input.origin.catalogSnapshot.catalogAssignment.locationId ===
          input.origin.locationId &&
        hasSameSerializableStructure(
          input.origin.catalogSnapshot.catalogAssignment,
          input.origin.catalogAssignment,
        ) &&
        hasSameSerializableStructure(
          input.origin.catalogSnapshot.workplace,
          input.workplace,
        ) &&
        hasSameSerializableStructure(
          input.origin.catalogSnapshot.services,
          input.services,
        ) &&
        hasSameSerializableStructure(
          input.origin.catalogSnapshot.offerings,
          input.offerings,
        ) &&
        isLocationGroupBookingEntryFeasibleFromCatalog(
          input.origin.catalogSnapshot,
        );

  if (
    draft.mode !== "group" ||
    !hasExactOrigin ||
    draft.participants.length < 2
  ) {
    return null;
  }

  const organizer = draft.participants.find(
    ({ participantId }) =>
      participantId === draft.organizerParticipantId,
  );

  if (!organizer || organizer.role !== "organizer") {
    return null;
  }

  const organizerSelections = getOrderedSelections(
    organizer.serviceLines,
  );
  const groupGuests = draft.participants
    .filter(
      ({ participantId }) =>
        participantId !== organizer.participantId,
    )
    .map((participant) => ({
      participantId: participant.participantId,
      selections: getOrderedSelections(participant.serviceLines),
    }));
  const rebuilt =
    input.origin.kind === "specialist-services"
      ? createSpecialistServicesAppointmentDraft({
          draftId: draft.draftId,
          mode: "group",
          assignment: input.origin.assignment,
          workplace: input.workplace,
          services: input.services,
          offerings: input.offerings,
          selections: organizerSelections,
          groupGuests,
        })
      : createLocationServicesAppointmentDraft({
          draftId: draft.draftId,
          mode: "group",
          locationId: input.origin.locationId,
          catalogAssignment: input.origin.catalogAssignment,
          workplace: input.workplace,
          services: input.services,
          offerings: input.offerings,
          catalogSnapshot: input.origin.catalogSnapshot,
          selections: organizerSelections,
          groupGuests,
          initialProfessionalUserLocationId:
            draft.entryPoint.kind === "location-services"
              ? draft.entryPoint.initialProfessionalUserLocationId
              : undefined,
        });

  if (!rebuilt.ok || !hasSameSerializableStructure(rebuilt.draft, draft)) {
    return null;
  }

  return {
    draftId: draft.draftId,
    organizerSelections: organizerSelections.map(cloneSelection),
    groupGuests: groupGuests.map((guest) => ({
      participantId: guest.participantId,
      selections: guest.selections.map(cloneSelection),
    })),
  };
}

export function resolveSpecialistGroupBookingResume(input: {
  draft: AppointmentBookingDraftV1;
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
}) {
  return resolveServicesGroupBookingResume({
    ...input,
    origin: {
      kind: "specialist-services",
      assignment: input.assignment,
    },
  });
}

/**
 * Location counterpart for the same versioned group-resume pointer. It
 * rebuilds through the exact route-owned Location snapshot, so a
 * Specialist-owned draft, stale catalog-owner assignment, or eligibility
 * revision mismatch can never hydrate the Location route.
 */
export function resolveLocationGroupBookingResume(input: {
  draft: AppointmentBookingDraftV1;
  locationId: string;
  catalogSnapshot: LocationAppointmentCatalog;
  catalogAssignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
}) {
  return resolveServicesGroupBookingResume({
    draft: input.draft,
    origin: {
      kind: "location-services",
      locationId: input.locationId,
      catalogSnapshot: input.catalogSnapshot,
      catalogAssignment: input.catalogAssignment,
    },
    workplace: input.workplace,
    services: input.services,
    offerings: input.offerings,
  });
}
